import React, { useState, useEffect } from 'react';
import { Search, SlidersHorizontal, X, RotateCcw, ChevronDown, Download } from 'lucide-react';
import { useCRM } from '../../context/CRMContext';
import { MultiSelect } from '../common/Common';
import './AdvancedSearch.css';

const asArr = (v) => (Array.isArray(v) ? v : v ? [v] : []);

const AdvancedSearch = ({ filters, setFilters, showLeadType = false, onSearch }) => {
  const [expanded, setExpanded] = useState(false);
  const [users, setUsers] = useState([]);
  const { masterData, fetchUsers } = useCRM();

  const sources = masterData?.sources || [];
  const statuses = masterData?.statuses || [];
  const projects = masterData?.projects || [];
  const propertyTypes = masterData?.property_types || masterData?.propertyTypes || [];
  const serviceTypes = masterData?.service_types || masterData?.serviceTypes || [];
  const formNames = masterData?.form_names || [];

  // Load users once when the panel is first expanded
  useEffect(() => {
    if (!expanded || users.length > 0) return;
    let cancelled = false;
    (async () => {
      const res = await fetchUsers();
      if (cancelled) return;
      const list = res?.success ? (Array.isArray(res.data) ? res.data : res.data?.users || []) : [];
      setUsers(list);
    })();
    return () => { cancelled = true; };
  }, [expanded, users.length, fetchUsers]);

  const updateFilter = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    // Trigger search on filter change (debounced via parent)
    if (key !== 'search') onSearch?.(newFilters);
  };

  const resetFilters = () => {
    const empty = {
      search: '', status: [], priority: [], source: [],
      service_type: [], property_type: [], project: [], assigned: '',
      assigned_to: [], form_name: [], date_from: '', date_to: '', lead_type: '',
    };
    setFilters(empty);
    onSearch?.(empty);
  };

  const hasValue = (v) => (Array.isArray(v) ? v.length > 0 : v && v !== '');

  const activeFilterCount = Object.entries(filters).filter(
    ([k, v]) => k !== 'search' && hasValue(v)
  ).length;

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') onSearch?.(filters);
  };

  // Resolve a filter value into a human-readable label (ID → name)
  const labelFor = (key, value) => {
    if (Array.isArray(value)) {
      return value.map(v => labelFor(key, v)).filter(Boolean).join(', ');
    }
    if (value === '' || value === null || value === undefined) return '';
    const str = String(value);
    switch (key) {
      case 'source': {
        const match = sources.find(s => String(s.src_id) === str);
        return match?.name || str;
      }
      case 'status': {
        const match = statuses.find(s => String(s.ls_id) === str);
        return match?.name || str;
      }
      case 'property_type': {
        const match = propertyTypes.find(t => String(t.pt_id) === str);
        return match?.name || str;
      }
      case 'service_type': {
        const match = serviceTypes.find(t => String(t.st_id) === str);
        return match?.name || str;
      }
      case 'project': {
        const match = projects.find(p => String(p.project_id) === str);
        return match?.name || str;
      }
      case 'assigned':
        return str === 'assigned' ? 'Assigned' : str === 'non_assigned' ? 'Unassigned' : str;
      case 'assigned_to': {
        const match = users.find(u => String(u.u_id) === str);
        return match?.username || str;
      }
      case 'lead_type':
        return str.charAt(0).toUpperCase() + str.slice(1);
      default:
        return str;
    }
  };

  const prettyKey = (k) => {
    const map = {
      source: 'Source', status: 'Status', service_type: 'Service Type', property_type: 'Property Type',
      project: 'Project', assigned: 'Assignment', assigned_to: 'Assigned To', lead_type: 'Lead Type',
      form_name: 'Form Name', date_from: 'From', date_to: 'To',
    };
    return map[k] || k.replace(/_/g, ' ');
  };

  return (
    <div className="adv-search">
      {/* Primary Search Bar */}
      <div className="adv-search__bar">
        <div className="adv-search__input-wrap">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search by name, phone, email..."
            value={filters.search || ''}
            onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
            onKeyDown={handleSearchKeyDown}
            className="adv-search__input"
          />
          {filters.search && (
            <button className="adv-search__clear-input" onClick={() => { updateFilter('search', ''); onSearch?.({ ...filters, search: '' }); }}>
              <X size={14} />
            </button>
          )}
        </div>
        <button
          type="button"
          className="adv-search__filter-mobile"
          onClick={() => setExpanded(!expanded)}
          aria-label="Open filters"
        >
          <SlidersHorizontal size={16} />
        </button>
        <button
          type="button"
          className="adv-search__export-mobile"
          onClick={() => {
            const exportBtn = document.querySelector('.tbl-export-btn');
            exportBtn?.click();
          }}
          aria-label="Export leads"
        >
          <Download size={16} />
        </button>
        <button
          className={`adv-search__toggle ${expanded ? 'adv-search__toggle--active' : ''}`}
          onClick={() => setExpanded(!expanded)}
        >
          <SlidersHorizontal size={16} />
          <span className="adv-search__toggle-label">Filters</span>
          {activeFilterCount > 0 && <span className="adv-search__count">{activeFilterCount}</span>}
          <ChevronDown size={14} className={`adv-search__chevron ${expanded ? 'adv-search__chevron--open' : ''}`} />
        </button>
        {activeFilterCount > 0 && (
          <button className="adv-search__reset" onClick={resetFilters}>
            <RotateCcw size={14} /> Reset
          </button>
        )}
      </div>

      {/* Expanded Advanced Filters */}
      {expanded && (
        <div className="adv-search__panel animate-fade-in">
          <h4 className="adv-search__panel-title">Advanced Filters</h4>
          <div className="adv-search__grid">
            <div className="adv-search__field adv-search__field--multi">
              <label>Source</label>
              <MultiSelect
                value={asArr(filters.source)}
                onChange={(arr) => updateFilter('source', arr)}
                options={sources.map(s => ({ value: s.src_id, label: s.name }))}
                placeholder="All Sources"
              />
            </div>
            <div className="adv-search__field adv-search__field--multi">
              <label>Status</label>
              <MultiSelect
                value={asArr(filters.status)}
                onChange={(arr) => updateFilter('status', arr)}
                options={statuses.map(s => ({ value: s.ls_id, label: s.name }))}
                placeholder="All Statuses"
              />
            </div>
            <div className="adv-search__field adv-search__field--multi">
              <label>Service Type</label>
              <MultiSelect
                value={asArr(filters.service_type)}
                onChange={(arr) => updateFilter('service_type', arr)}
                options={serviceTypes.map(t => ({ value: t.st_id, label: t.name }))}
                placeholder="All Services"
              />
            </div>
            <div className="adv-search__field adv-search__field--multi">
              <label>Property Type</label>
              <MultiSelect
                value={asArr(filters.property_type)}
                onChange={(arr) => updateFilter('property_type', arr)}
                options={propertyTypes.map(t => ({ value: t.pt_id, label: t.name }))}
                placeholder="All Types"
              />
            </div>
            <div className="adv-search__field adv-search__field--multi">
              <label>Project</label>
              <MultiSelect
                value={asArr(filters.project)}
                onChange={(arr) => updateFilter('project', arr)}
                options={projects.map(p => ({ value: p.project_id, label: p.name }))}
                placeholder="All Projects"
              />
            </div>
            <div className="adv-search__field">
              <label>Assignment</label>
              <select value={filters.assigned || ''} onChange={(e) => updateFilter('assigned', e.target.value)}>
                <option value="">All</option>
                <option value="assigned">Assigned</option>
                <option value="non_assigned">Unassigned</option>
              </select>
            </div>
            <div className="adv-search__field adv-search__field--multi">
              <label>Assigned To</label>
              <MultiSelect
                value={asArr(filters.assigned_to)}
                onChange={(arr) => updateFilter('assigned_to', arr)}
                options={users.map(u => ({
                  value: u.u_id,
                  label: `${u.username}${u.role_name ? ` (${u.role_name})` : ''}`,
                }))}
                placeholder="All Users"
              />
            </div>
            <div className="adv-search__field adv-search__field--multi">
              <label>Form Name</label>
              <MultiSelect
                value={asArr(filters.form_name)}
                onChange={(arr) => updateFilter('form_name', arr)}
                options={formNames.map(n => ({ value: n, label: n }))}
                placeholder="All Forms"
              />
            </div>
            {showLeadType && (
              <div className="adv-search__field">
                <label>Lead Type</label>
                <select value={filters.lead_type || ''} onChange={(e) => updateFilter('lead_type', e.target.value)}>
                  <option value="">All Types</option>
                  <option value="fresh">Fresh</option>
                  <option value="imported">Imported</option>
                </select>
              </div>
            )}
            <div className="adv-search__field">
              <label>Date From</label>
              <input type="date" value={filters.date_from || ''} onChange={(e) => updateFilter('date_from', e.target.value)} />
            </div>
            <div className="adv-search__field">
              <label>Date To</label>
              <input type="date" value={filters.date_to || ''} onChange={(e) => updateFilter('date_to', e.target.value)} />
            </div>
          </div>

          {activeFilterCount > 0 && (
            <div className="adv-search__active-filters">
              <span className="adv-search__active-label">Active filters:</span>
              {Object.entries(filters)
                .filter(([k, v]) => k !== 'search' && hasValue(v))
                .map(([k, v]) => (
                  <span key={k} className="adv-search__active-chip">
                    {prettyKey(k)}: <strong>{labelFor(k, v)}</strong>
                    <button onClick={() => updateFilter(k, Array.isArray(v) ? [] : '')}>
                      <X size={10} />
                    </button>
                  </span>
                ))
              }
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdvancedSearch;
